import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Save, Plus, Trash2, Image, Link, QrCode, Eye, Edit } from 'lucide-react';
import { serviceApiService } from '../services/serviceApi';
import { 
  servicePageConfigApiService, 
  ServicePageContent, 
  CreateServicePageContentRequest 
} from '../services/servicePageConfigApi';
import ImageUploader from './ImageUploader';
import PDFUploader from './PDFUploader';

// Composant ContentEditor séparé pour éviter la re-création
interface ContentEditorProps {
  editingContent: ServicePageContent | null;
  onContentDataChange: (field: string, value: string) => void;
  onContentTypeChange: (contentType: 'text' | 'image' | 'link' | 'qr_code' | 'pdf') => void;
  onSave: () => void;
  onCancel: () => void;
  onSectionSave?: (sectionData: any) => void;
  savedSections?: {[key: string]: any};
}

const ContentEditor: React.FC<ContentEditorProps> = ({
  editingContent,
  onContentDataChange,
  onContentTypeChange,
  onSave,
  onCancel,
  onSectionSave,
  savedSections = {}
}) => {
  if (!editingContent) return null;

  // Vérifier quelles sections ont été sauvegardées
  const contentId = editingContent.id || 'new';
  const isTextSaved = savedSections[`${contentId}_text`];
  const isImageSaved = savedSections[`${contentId}_image`];
  const isLinkSaved = savedSections[`${contentId}_link`];
  const isQRSaved = savedSections[`${contentId}_qr_code`];

  // Fonctions pour sauvegarder chaque section
  const saveTextSection = () => {
    if (onSectionSave && editingContent.content_data.text) {
      onSectionSave({ type: 'text', data: { text: editingContent.content_data.text } });
    }
  };

  const saveImageSection = () => {
    if (onSectionSave && editingContent.content_data.image_url) {
      onSectionSave({ type: 'image', data: { image_url: editingContent.content_data.image_url } });
    }
  };

  const saveLinkSection = () => {
    if (onSectionSave && (editingContent.content_data.link_text || editingContent.content_data.link_url)) {
      onSectionSave({ 
        type: 'link', 
        data: { 
          link_text: editingContent.content_data.link_text, 
          link_url: editingContent.content_data.link_url 
        } 
      });
    }
  };

  const saveQRSection = () => {
    if (onSectionSave && (editingContent.content_data.qr_data || editingContent.content_data.qr_image_url)) {
      onSectionSave({ 
        type: 'qr_code', 
        data: { 
          qr_label: editingContent.content_data.qr_label,
          qr_data: editingContent.content_data.qr_data,
          qr_mode: editingContent.content_data.qr_mode,
          qr_image_url: editingContent.content_data.qr_image_url
        } 
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingContent.id ? 'Modifier le contenu' : 'Ajouter du contenu'}
          </h3>
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              💡 <strong>Nouveau :</strong> Vous pouvez maintenant sauvegarder chaque section individuellement avec les boutons verts. 
              Vos données ne seront plus perdues quand vous changez de type de contenu !
            </p>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Type de contenu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de contenu
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'text', label: 'Texte explicatif', icon: '📝', saved: isTextSaved },
                { value: 'image', label: 'Image', icon: '🖼️', saved: isImageSaved },
                { value: 'link', label: 'Lien cliquable', icon: '🔗', saved: isLinkSaved },
                { value: 'qr_code', label: 'QR Code', icon: '📱', saved: isQRSaved },
                { value: 'pdf', label: 'Document PDF', icon: '📄', saved: false }
              ].map((type) => (
                <button
                  key={type.value}
                  onClick={() => onContentTypeChange(type.value as any)}
                  className={`relative p-3 rounded-lg border-2 transition-all ${
                    editingContent.content_type === type.value
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {type.saved && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                  <div className="text-center">
                    <div className="text-2xl mb-1">{type.icon}</div>
                    <div className="text-sm font-medium">{type.label}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Contenu selon le type */}
          {editingContent.content_type === 'text' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Texte explicatif
              </label>
              <textarea
                value={editingContent.content_data.text || ''}
                onChange={(e) => onContentDataChange('text', e.target.value)}
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Saisissez votre texte explicatif ici..."
              />
              {editingContent.content_data.text && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveTextSection}
                    className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-md text-sm font-medium transition-colors"
                  >
                    ✓ Sauvegarder ce texte
                  </button>
                </div>
              )}
            </div>
          )}

          {editingContent.content_type === 'image' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Image
                </label>
                
                {/* Upload d'image */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">📤 Upload d'image</h4>
                  <ImageUploader
                    onImageSelect={(imageUrl) => onContentDataChange('image_url', imageUrl)}
                    currentImageUrl={editingContent.content_data.image_url}
                    placeholder="Glissez une image ici ou cliquez pour sélectionner"
                  />
                </div>

                {/* Ou URL manuelle */}
                <div>
                  <h4 className="text-sm font-medium text-gray-600 mb-2">🔗 Ou saisissez une URL</h4>
                  <input
                    type="url"
                    value={editingContent.content_data.image_url || ''}
                    onChange={(e) => onContentDataChange('image_url', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                {editingContent.content_data.image_url && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={saveImageSection}
                      className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-md text-sm font-medium transition-colors"
                    >
                      ✓ Sauvegarder cette image
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {editingContent.content_type === 'link' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Texte du lien (ex: "Cliquez ici")
                </label>
                <input
                  type="text"
                  value={editingContent.content_data.link_text || ''}
                  onChange={(e) => onContentDataChange('link_text', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Cliquez ici pour en savoir plus"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de destination
                </label>
                <input
                  type="url"
                  value={editingContent.content_data.link_url || ''}
                  onChange={(e) => onContentDataChange('link_url', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com"
                />
              </div>

              {(editingContent.content_data.link_text || editingContent.content_data.link_url) && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveLinkSection}
                    className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-md text-sm font-medium transition-colors"
                  >
                    ✓ Sauvegarder ce lien
                  </button>
                </div>
              )}
            </div>
          )}

          {editingContent.content_type === 'qr_code' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Libellé du QR Code
                </label>
                <input
                  type="text"
                  value={editingContent.content_data.qr_label || ''}
                  onChange={(e) => onContentDataChange('qr_label', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Scannez pour plus d'infos"
                />
              </div>

              {/* Choix du mode QR Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Mode QR Code
                </label>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => onContentDataChange('qr_mode', 'generate')}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      (editingContent.content_data.qr_mode || 'generate') === 'generate'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">🔧</div>
                    <div className="text-sm font-medium">Générer automatiquement</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => onContentDataChange('qr_mode', 'upload')}
                    className={`p-3 rounded-lg border-2 transition-all text-center ${
                      editingContent.content_data.qr_mode === 'upload'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-lg mb-1">📤</div>
                    <div className="text-sm font-medium">Upload image QR</div>
                  </button>
                </div>
              </div>

              {/* Mode génération automatique */}
              {(editingContent.content_data.qr_mode || 'generate') === 'generate' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Données du QR Code (URL, texte, etc.)
                  </label>
                  <input
                    type="text"
                    value={editingContent.content_data.qr_data || ''}
                    onChange={(e) => onContentDataChange('qr_data', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com ou texte"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Le QR code sera généré automatiquement à partir de ces données
                  </p>
                </div>
              )}

              {/* Mode upload d'image QR */}
              {editingContent.content_data.qr_mode === 'upload' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image QR Code existante
                  </label>
                  <ImageUploader
                    onImageSelect={(imageUrl) => onContentDataChange('qr_image_url', imageUrl)}
                    currentImageUrl={editingContent.content_data.qr_image_url}
                    placeholder="Glissez votre QR code ici ou cliquez pour sélectionner"
                    accept="image/*"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Uploadez une image QR code déjà créée
                  </p>
                </div>
              )}

              {(editingContent.content_data.qr_data || editingContent.content_data.qr_image_url) && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveQRSection}
                    className="px-3 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-md text-sm font-medium transition-colors"
                  >
                    ✓ Sauvegarder ce QR Code
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Section PDF */}
          {editingContent.content_type === 'pdf' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du PDF
                </label>
                <input
                  type="text"
                  value={editingContent.content_data.pdf_title || ''}
                  onChange={(e) => onContentDataChange('pdf_title', e.target.value)}
                  placeholder="Ex: Télécharger notre brochure"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnelle)
                </label>
                <input
                  type="text"
                  value={editingContent.content_data.pdf_description || ''}
                  onChange={(e) => onContentDataChange('pdf_description', e.target.value)}
                  placeholder="Courte description du contenu"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fichier PDF
                </label>
                <PDFUploader
                  onPDFSelect={(pdfId, fileName) => {
                    onContentDataChange('pdf_id', pdfId);
                    onContentDataChange('pdf_file_name', fileName);
                  }}
                  currentPDF={editingContent.content_data.pdf_id}
                  currentName={editingContent.content_data.pdf_file_name}
                />
              </div>

              {editingContent.content_data.pdf_id && editingContent.content_data.pdf_title && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => onSectionSave?.({ 
                          type: 'pdf', 
                          data: { 
                            pdf_title: editingContent.content_data.pdf_title,
                            pdf_id: editingContent.content_data.pdf_id,
                            pdf_description: editingContent.content_data.pdf_description,
                            pdf_file_name: editingContent.content_data.pdf_file_name
                          } 
                        })}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    ✅ Sauvegarder cette section PDF
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
      </div>
    );
};

const ServicesPageConfig: React.FC = () => {
  const [services, setServices] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<string>('');
  const [contents, setContents] = useState<ServicePageContent[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingContent, setEditingContent] = useState<ServicePageContent | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [savedSections, setSavedSections] = useState<{[key: string]: any}>({});

  useEffect(() => {
    loadServices();
  }, []);

  useEffect(() => {
    if (selectedService) {
      loadServiceContents(selectedService);
    }
  }, [selectedService]);

  const loadServices = async () => {
    try {
      console.log('Chargement des services...');
      const servicesData = await serviceApiService.getServices();
      console.log('Services récupérés:', servicesData);
      setServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des services:', error);
      setServices([]);
    }
  };

  const loadServiceContents = async (serviceId: string) => {
    try {
      console.log('📥 Chargement des contenus pour le service:', serviceId);
      setLoading(true);
      const contentsData = await servicePageConfigApiService.getServicePageContents(serviceId);
      console.log('📋 Contenus récupérés du serveur:', contentsData);
      console.log('📊 Nombre de contenus:', contentsData.length);
      
      if (contentsData.length > 0) {
        console.log('📝 Détail des contenus:');
        contentsData.forEach((content, index) => {
          console.log(`  ${index + 1}. ${content.content_type} (ID: ${content.id})`, content.content_data);
        });
      }
      
      setContents(contentsData);
      console.log('✅ Contenus mis à jour dans l\'état local');
    } catch (error: any) {
      console.error('❌ Erreur lors du chargement du contenu:', error);
      console.error('❌ Détails:', error.response?.data || error.message);
      setContents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContent = () => {
    const newContent: ServicePageContent = {
      id: '',
      service_id: selectedService,
      service_name: services.find(s => s.id === selectedService)?.name || '',
      content_type: 'text',
      content_data: { text: '' },
      order: contents.length + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setEditingContent(newContent);
    setShowAddModal(true);
  };

  const handleSaveContent = async () => {
    if (!editingContent) {
      console.log('❌ Pas de contenu à sauvegarder');
      return;
    }
    
    console.log('🚀 === DÉBUT SAUVEGARDE ===');
    console.log('📝 Contenu à sauvegarder:', {
      id: editingContent.id,
      service_id: editingContent.service_id,
      content_type: editingContent.content_type,
      content_data: editingContent.content_data,
      order: editingContent.order
    });
    
    try {
      if (editingContent.id && contents.find(c => c.id === editingContent.id)) {
        // Mise à jour
        console.log('🔄 Mise à jour du contenu existant:', editingContent.id);
        const updatedContent = await servicePageConfigApiService.updateServicePageContent(
          editingContent.id,
          {
            content_data: editingContent.content_data,
            order: editingContent.order
          }
        );
        console.log('✅ Contenu mis à jour:', updatedContent);
      } else {
        // Ajout
        console.log('➕ Création d\'un nouveau contenu pour le service:', editingContent.service_id);
        const createRequest: CreateServicePageContentRequest = {
          service_id: editingContent.service_id,
          content_type: editingContent.content_type,
          content_data: editingContent.content_data,
          order: editingContent.order
        };
        console.log('📤 Requête de création:', createRequest);
        const newContent = await servicePageConfigApiService.createServicePageContent(createRequest);
        console.log('✅ Nouveau contenu créé avec succès:', newContent);
      }
      
      // Recharger les contenus depuis le serveur pour s'assurer de la cohérence
      console.log('🔄 Rechargement des contenus depuis le serveur...');
      await loadServiceContents(editingContent.service_id);
      console.log('✅ Contenus rechargés');
      
      setEditingContent(null);
      setShowAddModal(false);
      setSavedSections({}); // Vider les sections sauvegardées temporairement
      
      console.log('🎉 === SAUVEGARDE TERMINÉE ===');
    } catch (error: any) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      console.error('❌ Détails de l\'erreur:', error.response?.data || error.message);
      alert(`Erreur lors de la sauvegarde du contenu: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteContent = async (contentId: string) => {
    try {
      console.log('Suppression du contenu:', contentId);
      await servicePageConfigApiService.deleteServicePageContent(contentId);
      
      // Recharger les contenus depuis le serveur
      console.log('Rechargement des contenus après suppression...');
      if (selectedService) {
        await loadServiceContents(selectedService);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du contenu');
    }
  };

  // Handlers optimisés pour éviter la perte de focus
  const handleContentDataChange = useCallback((field: string, value: string) => {
    setEditingContent(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        content_data: {
          ...prev.content_data,
          [field]: value
        }
      };
    });
  }, []);

  const handleContentTypeChange = useCallback((contentType: 'text' | 'image' | 'link' | 'qr_code' | 'pdf') => {
    setEditingContent(prev => {
      if (!prev) return prev;
      
      // Conserver les données existantes au lieu de les effacer
      const existingData = prev.content_data || {};
      
      return {
        ...prev,
        content_type: contentType,
        content_data: existingData // Garder toutes les données existantes
      };
    });
  }, []);

  // Fonction pour gérer les sauvegardes de sections
  const handleSectionSave = useCallback((sectionData: any) => {
    const sectionKey = `${editingContent?.id || 'new'}_${sectionData.type}`;
    setSavedSections(prev => ({
      ...prev,
      [sectionKey]: sectionData.data
    }));
    
    // Afficher une notification de succès
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
    notification.textContent = `✓ Section ${sectionData.type} sauvegardée temporairement`;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      document.body.removeChild(notification);
    }, 2000);
  }, [editingContent]);

  console.log('Render - Services:', services.length, 'Selected:', selectedService, 'Contents:', contents.length);

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Configuration de la page Services
        </h2>
        <p className="text-gray-600">
          Sélectionnez un service pour personnaliser son contenu avec du texte, des images, des liens et des QR codes.
        </p>
        
        {/* Debug info */}
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
          <strong>Debug:</strong> Services chargés: {services.length} | Service sélectionné: {selectedService || 'Aucun'} | Contenus: {contents.length}
          <div className="mt-2 space-x-2">
            <button 
              onClick={loadServices}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs"
            >
              Recharger services
            </button>
            {selectedService && (
              <button 
                onClick={() => loadServiceContents(selectedService)}
                className="px-2 py-1 bg-green-500 text-white rounded text-xs"
              >
                Recharger contenus
              </button>
            )}
            <button 
              onClick={async () => {
                console.log('=== TEST COMPLET ===');
                console.log('1. Test des services...');
                try {
                  const servicesData = await serviceApiService.getServices();
                  console.log('Services récupérés:', servicesData);
                  setServices(Array.isArray(servicesData) ? servicesData : []);
                  
                  if (servicesData && servicesData.length > 0) {
                    console.log('2. Test des contenus pour le premier service:', servicesData[0].id);
                    const contentsData = await servicePageConfigApiService.getServicePageContents(servicesData[0].id);
                    console.log('Contenus récupérés:', contentsData);
                    setContents(contentsData);
                  }
                } catch (error) {
                  console.error('Erreur dans le test:', error);
                }
                console.log('=== FIN TEST ===');
              }}
              className="px-2 py-1 bg-purple-500 text-white rounded text-xs"
            >
              Test complet
            </button>
            {selectedService && (
              <button 
                onClick={async () => {
                  console.log('🧪 === TEST CRÉATION CONTENU ===');
                  try {
                    const testContent = {
                      service_id: selectedService,
                      content_type: 'text' as const,
                      content_data: { text: 'Test automatique - ' + new Date().toLocaleTimeString() },
                      order: contents.length + 1
                    };
                    console.log('📤 Création du contenu test:', testContent);
                    const result = await servicePageConfigApiService.createServicePageContent(testContent);
                    console.log('✅ Contenu test créé:', result);
                    await loadServiceContents(selectedService);
                    console.log('🎉 Test terminé avec succès!');
                  } catch (error) {
                    console.error('❌ Erreur dans le test:', error);
                  }
                }}
                className="px-2 py-1 bg-orange-500 text-white rounded text-xs"
              >
                Test création
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Grille des services sous forme de cartes */}
      {!selectedService ? (
        services.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚙️</span>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun service disponible</h3>
            <p className="text-gray-500">Créez d'abord des services pour pouvoir configurer leur contenu.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
            <motion.div
              key={service.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer"
              onClick={() => setSelectedService(service.id)}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {service.name}
                    </h3>
                    {service.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {service.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 p-2 bg-blue-50 rounded-lg">
                    <span className="text-2xl">⚙️</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const serviceContents = contents.filter(c => String(c.service_id) === String(service.id));
                      const contentCount = serviceContents.length;
                      return (
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            contentCount > 0 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {contentCount > 0 ? '✓' : '○'} {contentCount} contenu{contentCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex items-center text-blue-600 text-sm font-medium">
                    Configurer
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>
            ))}
          </div>
        )
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSelectedService('')}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Retour aux services"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {services.find(s => s.id === selectedService)?.name}
                  </h3>
                  <p className="text-sm text-gray-500">Configuration du contenu</p>
                </div>
              </div>
              <button
                onClick={handleAddContent}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Ajouter du contenu</span>
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Debug temporaire */}
            {contents.length > 0 && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs">
                <strong>DEBUG:</strong> Service sélectionné: "{selectedService}" (type: {typeof selectedService}) | 
                Contenus: {contents.map(c => `ID:${c.id} ServiceID:${c.service_id}(${typeof c.service_id})`).join(', ')}
              </div>
            )}
            
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Chargement...</p>
              </div>
            ) : (() => {
              const filteredContents = contents.filter(c => String(c.service_id) === String(selectedService));
              console.log('🔍 Filtrage des contenus:', {
                selectedService,
                selectedServiceType: typeof selectedService,
                totalContents: contents.length,
                filteredContents: filteredContents.length,
                contentServiceIds: contents.map(c => ({ id: c.id, service_id: c.service_id, type: typeof c.service_id }))
              });
              return filteredContents.length === 0;
            })() ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📝</span>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun contenu configuré</h3>
                <p className="text-gray-500 mb-4">
                  Ce service n'a pas encore de contenu personnalisé
                </p>
                <button
                  onClick={handleAddContent}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter le premier contenu</span>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const filteredContents = contents.filter(c => String(c.service_id) === String(selectedService));
                  console.log(`📋 Contenus filtrés pour service ${selectedService}:`, filteredContents);
                  const pdfContents = filteredContents.filter(c => c.content_type === 'pdf');
                  console.log(`📄 PDF dans admin pour service ${selectedService}:`, pdfContents);
                  return filteredContents;
                })().map((content, index) => (
                  <div key={content.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-medium">
                          {index + 1}
                        </span>
                        <div className="flex items-center space-x-2">
                          {content.content_type === 'text' && <span>📝</span>}
                          {content.content_type === 'image' && <span>🖼️</span>}
                          {content.content_type === 'link' && <span>🔗</span>}
                          {content.content_type === 'qr_code' && <span>📱</span>}
                          {content.content_type === 'pdf' && <span>📄</span>}
                          <span className="font-medium text-gray-900 capitalize">
                            {content.content_type.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingContent(content);
                            setShowAddModal(true);
                          }}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContent(content.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600 space-y-1">
                      {content.content_type === 'text' && (
                        <div>
                          <strong>Texte:</strong> <span className="line-clamp-2">{content.content_data.text || 'Vide'}</span>
                        </div>
                      )}
                      {content.content_type === 'image' && (
                        <div>
                          <strong>Image URL:</strong> {content.content_data.image_url || 'Non définie'}
                          {content.content_data.image_url && (
                            <img src={content.content_data.image_url} alt="Aperçu" className="mt-2 max-w-32 h-20 object-cover rounded" />
                          )}
                        </div>
                      )}
                      {content.content_type === 'link' && (
                        <div>
                          <strong>Texte du lien:</strong> "{content.content_data.link_text || 'Non défini'}"<br/>
                          <strong>URL:</strong> {content.content_data.link_url || 'Non définie'}
                        </div>
                      )}
                      {content.content_type === 'qr_code' && (
                        <div>
                          <strong>Libellé:</strong> {content.content_data.qr_label || 'Non défini'}<br/>
                          <strong>Mode:</strong> {content.content_data.qr_mode || 'generate'}<br/>
                          {content.content_data.qr_mode === 'generate' ? (
                            <span><strong>Données:</strong> {content.content_data.qr_data || 'Non définies'}</span>
                          ) : (
                            <span><strong>Image QR:</strong> {content.content_data.qr_image_url || 'Non définie'}</span>
                          )}
                        </div>
                      )}
                      {content.content_type === 'pdf' && (
                        <div>
                          <strong>Titre:</strong> {content.content_data.pdf_title || 'Non défini'}<br/>
                          <strong>Description:</strong> {content.content_data.pdf_description || 'Aucune'}<br/>
                          <strong>URL PDF:</strong> {content.content_data.pdf_url ? 'Définie' : 'Non définie'}
                          {content.content_data.pdf_url && (
                            <a href={content.content_data.pdf_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:underline text-xs">
                              📄 Voir le PDF
                            </a>
                          )}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-2">
                        ID: {content.id} | Créé: {new Date(content.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showAddModal && (
        <ContentEditor
          editingContent={editingContent}
          onContentDataChange={handleContentDataChange}
          onContentTypeChange={handleContentTypeChange}
          onSave={handleSaveContent}
          onCancel={() => {
            setEditingContent(null);
            setShowAddModal(false);
          }}
          onSectionSave={handleSectionSave}
          savedSections={savedSections}
        />
      )}
    </div>
  );
};

export default ServicesPageConfig;
